import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Steps } from 'primereact/steps';
import { Avatar } from 'primereact/avatar';
import { Tag } from 'primereact/tag';
import { Divider } from 'primereact/divider';
import { ProgressBar } from 'primereact/progressbar';
import { Toast } from 'primereact/toast';
import { Skeleton } from 'primereact/skeleton';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';

import ProfileService from '../../services/agentProfile/ProfileService';
import profileCompletionService from '../../services/profileCompletionService';
import BasicInfoStep        from '../../components/agentProfile/wizard/BasicInfoStep';
import WorkExperienceStep   from '../../components/agentProfile/wizard/WorkExperienceStep';
import EducationStep        from '../../components/agentProfile/wizard/EducationStep';
import CertificationsStep   from '../../components/agentProfile/wizard/CertificationsStep';
import SkillsLanguagesStep  from '../../components/agentProfile/wizard/SkillsLanguagesStep';
import ReviewStep           from '../../components/agentProfile/wizard/ReviewStep';

/**
 * Edit Profile — v2.0 wizard.
 *
 *   • Production wizard step components (BasicInfo / Work / Education /
 *     Certifications / Skills+Languages / Review) — same forms that already
 *     speak the saveProfile API.
 *   • Prototype's polished outer shell — completion meter, mobile-first
 *     clickable numbered circles, per-step hero card, sticky bottom action
 *     bar, ?step=<key> deep-link.
 *
 * Migration preserves every production behavior:
 *   • loadProfile + skills/languages object→string transform on read
 *   • Date → ISO date conversion on save
 *   • Mandatory-field validation pre-save
 *   • profileCompletionService.forceRefresh() to update topbar / sidebar
 *   • Photo upload + delete (now embedded in Basic Info step, not a global
 *     banner — matches the prototype)
 *   • Auto-redirect back to /app/my-agent-profile on success / cancel
 *   • Unsaved-changes guard when navigating away
 */
const STEPS = [
    { key: 'basic',     label: 'Basic info',         shortLabel: 'Basic',   icon: 'pi-id-card',      Component: BasicInfoStep },
    { key: 'work',      label: 'Work experience',    shortLabel: 'Work',    icon: 'pi-briefcase',    Component: WorkExperienceStep },
    { key: 'education', label: 'Education',          shortLabel: 'Edu',     icon: 'pi-book',         Component: EducationStep },
    { key: 'certs',     label: 'Certifications',     shortLabel: 'Certs',   icon: 'pi-verified',     Component: CertificationsStep },
    { key: 'skills',    label: 'Skills & languages', shortLabel: 'Skills',  icon: 'pi-star',         Component: SkillsLanguagesStep },
    { key: 'review',    label: 'Review',             shortLabel: 'Review',  icon: 'pi-check-square', Component: ReviewStep },
];

// ─── Live profile-completion calculator ──────────────────────────────────
// Mirrors the weights from the prototype so the meter responds in real time
// as the user fills fields. Backend's stored % is recalculated on save.
const calcCompletion = (d) => {
    if (!d) return 0;
    let score = 0;
    const add = (ok, pts) => { if (ok) score += pts; };
    add(d.full_name,                                    8);
    add(d.email,                                        4);
    add(d.phone && String(d.phone).length === 10,       4);
    add(d.headline,                                     6);
    add(d.bio && d.bio.length > 30,                     6);
    add(d.current_company,                              4);
    add(d.location_city,                                2);
    add((d.roles || []).length > 0,                     6);
    add((d.employment_types || []).length > 0,          4);
    add((d.product_expertise || []).length > 0,        10);
    add((d.expertise || []).length > 0,                 6);
    add((d.service_areas || []).length > 0,             8);
    add(((d.work_experiences || []).length
        + (d.salaried_experiences || []).length
        + (d.self_employed_experiences || []).length) > 0, 12);
    add((d.education || []).length > 0,                10);
    add((d.certifications || []).length > 0,            4);
    add((d.skills || []).length > 2,                    3);
    add((d.languages || []).length > 0,                 3);
    return Math.min(score, 100);
};

const EditProfile = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const toast = useRef(null);
    const stepsRef = useRef(null);
    const profilePhotoInputRef = useRef(null);
    const coverPhotoInputRef = useRef(null);

    // ─── Load + form state ───────────────────────────────────────────────
    const [formData, setFormData] = useState(null);
    const [originalData, setOriginalData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [profilePhotoUrl, setProfilePhotoUrl] = useState(null);
    const [coverPhotoUrl, setCoverPhotoUrl] = useState(null);
    const [uploadingPhoto, setUploadingPhoto] = useState(false);
    const [uploadingCover, setUploadingCover] = useState(false);
    const [photoTimestamp, setPhotoTimestamp] = useState(Date.now());
    const [coverTimestamp, setCoverTimestamp] = useState(Date.now());

    // ─── Step state ──────────────────────────────────────────────────────
    // Honour ?step=<key> so the pencil-edits on /app/my-agent-profile can
    // deep-link straight to the right step (Experience / Education / Certs).
    const initialStepIndex = useMemo(() => {
        const key = searchParams.get('step');
        if (!key) return 0;
        const idx = STEPS.findIndex((s) => s.key === key);
        return idx >= 0 ? idx : 0;
    }, [searchParams]);
    const [stepIndex, setStepIndex] = useState(initialStepIndex);

    // Re-honour the ?step= param if it changes mid-session (e.g. user clicks
    // a different pencil from MyAgentProfile and lands here again).
    useEffect(() => {
        setStepIndex(initialStepIndex);
    }, [initialStepIndex]);

    // ─── Mount: load profile ─────────────────────────────────────────────
    useEffect(() => {
        loadProfile();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadProfile = async () => {
        try {
            setLoading(true);
            const response = await ProfileService.getMyProfile();

            if (response.success) {
                setProfilePhotoUrl(response.data.profile_photo_url_full || null);
                setCoverPhotoUrl(response.data.cover_photo_url_full || null);
                setPhotoTimestamp(Date.now());
                setCoverTimestamp(Date.now());

                // Backend returns skills as [{ skill_name, ... }];
                // wizard expects ['skill_name_1', 'skill_name_2', ...].
                const transformedSkills = Array.isArray(response.data.skills)
                    ? response.data.skills
                          .map((s) => (typeof s === 'object' ? s.skill_name : s))
                          .filter(Boolean)
                    : [];

                const transformedLanguages = Array.isArray(response.data.languages)
                    ? response.data.languages
                          .map((l) => (typeof l === 'object' ? l.language_name : l))
                          .filter(Boolean)
                    : [];

                const profileData = {
                    // Basic info
                    full_name: response.data.full_name || '',
                    email: response.data.email || '',
                    phone: response.data.phone || '',
                    headline: response.data.headline || '',
                    bio: response.data.bio || '',
                    location_city: response.data.location_city || '',
                    location_state: response.data.location_state || '',
                    location_country: response.data.location_country || 'India',
                    current_company: response.data.current_company ?? '',
                    current_designation: response.data.current_designation ?? '',
                    experience_years:
                        response.data.experience_years !== null && response.data.experience_years !== undefined
                            ? parseFloat(response.data.experience_years)
                            : 0,

                    // Account visibility switch — false = public, true = private
                    is_private_account: !!response.data.is_private_account,

                    // Product expertise + roles
                    product_expertise:  Array.isArray(response.data.product_expertise)  ? response.data.product_expertise  : [],
                    expertise:          Array.isArray(response.data.expertise)          ? response.data.expertise          : [],
                    specializations:    Array.isArray(response.data.specializations)    ? response.data.specializations    : [],
                    roles:              Array.isArray(response.data.roles)              ? response.data.roles              : [],
                    employment_types:   Array.isArray(response.data.employment_types)   ? response.data.employment_types   : [],
                    service_areas:          Array.isArray(response.data.service_areas)          ? response.data.service_areas          : [],
                    associated_companies:   Array.isArray(response.data.associated_companies)   ? response.data.associated_companies   : [],

                    // Relations — separated by employment type
                    work_experiences:           response.data.work_experiences           || [],
                    salaried_experiences:       response.data.salaried_experiences       || [],
                    self_employed_experiences:  response.data.self_employed_experiences  || [],
                    education:                  response.data.education                  || [],
                    certifications:             response.data.certifications             || [],
                    skills:                     transformedSkills,
                    languages:                  transformedLanguages,
                };

                setFormData(profileData);
                // Deep clone for the dirty-check baseline.
                setOriginalData(JSON.parse(JSON.stringify(profileData)));
            }
        } catch (error) {
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to load profile data',
                life: 3000,
            });
            // No profile → bounce back so MyAgentProfile can run its 404
            // redirect to /app/create-profile.
            setTimeout(() => navigate('/app/my-agent-profile'), 1500);
        } finally {
            setLoading(false);
        }
    };

    // ─── Step handlers ───────────────────────────────────────────────────
    const handleStepData = (stepData) => {
        setFormData((prev) => ({ ...prev, ...stepData }));
    };

    const goTo = (i) => {
        const next = Math.max(0, Math.min(STEPS.length - 1, i));
        setStepIndex(next);
        // On mobile, scroll the step indicator card back into view so the
        // user knows where they are in the flow.
        stepsRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
    };
    const handleNext = () => goTo(stepIndex + 1);
    const handlePrevious = () => goTo(stepIndex - 1);
    const handleSkipToReview = () => goTo(STEPS.length - 1);

    // ─── Dirty check + validation ────────────────────────────────────────
    const hasChanges = useMemo(() => {
        if (!originalData || !formData) return false;
        return JSON.stringify(originalData) !== JSON.stringify(formData);
    }, [formData, originalData]);

    const validateProfile = (d) => {
        const errors = {};
        const required = [
            ['full_name',          'Full Name'],
            ['email',              'Email'],
            ['phone',              'Phone Number'],
            ['headline',           'Professional Headline'],
            ['bio',                'About / Bio'],
            ['current_company',    'Current Company'],
        ];
        required.forEach(([field, label]) => {
            const value = d[field];
            if (value === null || value === undefined || String(value).trim() === '') {
                errors[field] = `${label} is required`;
            }
        });
        if (d.experience_years === null || d.experience_years === undefined) {
            errors.experience_years = 'Years of Experience is required';
        }
        if (!d.roles || d.roles.length === 0) {
            errors.roles = 'At least one professional role is required';
        }
        if (!d.product_expertise || d.product_expertise.length === 0) {
            errors.product_expertise = 'At least one product expertise is required';
        }
        if (!d.expertise || d.expertise.length === 0) {
            errors.expertise = 'At least one area of expertise is required';
        }
        if (!d.service_areas || d.service_areas.length === 0) {
            errors.service_areas = 'At least one service area is required';
        }
        return { isValid: Object.keys(errors).length === 0, errors };
    };

    // ─── Submit ──────────────────────────────────────────────────────────
    const handleSubmit = async () => {
        if (!hasChanges) {
            toast.current?.show({
                severity: 'info',
                summary: 'No Changes',
                detail: 'No changes were made to your profile',
                life: 2500,
            });
            return;
        }
        const validation = validateProfile(formData);
        if (!validation.isValid) {
            const errorMessages = Object.values(validation.errors);
            toast.current?.show({
                severity: 'error',
                summary: 'Validation Failed',
                detail: (
                    <div>
                        <p className="mb-2">Please fill in all mandatory fields:</p>
                        <ul className="pl-3 mb-0">
                            {errorMessages.map((msg, idx) => (
                                <li key={idx}>{msg}</li>
                            ))}
                        </ul>
                    </div>
                ),
                life: 6000,
            });
            return;
        }

        try {
            setSaving(true);

            const toIso = (v) => (v instanceof Date ? v.toISOString().split('T')[0] : v);
            const dataToSave = {
                ...formData,
                education: formData.education?.map((edu) => ({
                    ...edu,
                    start_date: toIso(edu.start_date),
                    end_date:   toIso(edu.end_date),
                })),
                work_experiences: formData.work_experiences?.map((work) => ({
                    ...work,
                    start_date: toIso(work.start_date),
                    end_date:   toIso(work.end_date),
                })),
                salaried_experiences: formData.salaried_experiences?.map((work) => ({
                    ...work,
                    start_date: toIso(work.start_date),
                    end_date:   toIso(work.end_date),
                })),
                self_employed_experiences: formData.self_employed_experiences?.map((biz) => ({
                    ...biz,
                    start_date: toIso(biz.start_date),
                    end_date:   toIso(biz.end_date),
                })),
                certifications: formData.certifications?.map((cert) => ({
                    ...cert,
                    issue_date:      toIso(cert.issue_date),
                    expiration_date: toIso(cert.expiration_date),
                })),
            };

            const response = await ProfileService.saveProfile(dataToSave);
            if (response.success) {
                toast.current?.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Profile updated. Redirecting…',
                    life: 1500,
                });
                // Refresh the global completion-percentage cache so any topbar
                // / sidebar badges update in time for the next page.
                await profileCompletionService.forceRefresh();
                setTimeout(() => navigate('/app/my-agent-profile'), 800);
            } else {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: response.message || 'Failed to update profile',
                    life: 3000,
                });
            }
        } catch (error) {
            let errorDetail = 'Failed to update profile';
            if (error.response?.data?.message) {
                errorDetail = error.response.data.message;
            } else if (error.response?.data?.errors) {
                const backendErrors = Object.values(error.response.data.errors).flat();
                errorDetail = (
                    <div>
                        <p className="mb-2">Validation errors:</p>
                        <ul className="pl-3 mb-0">
                            {backendErrors.map((msg, idx) => (
                                <li key={idx}>{msg}</li>
                            ))}
                        </ul>
                    </div>
                );
            }
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: errorDetail,
                life: 5000,
            });
        } finally {
            setSaving(false);
        }
    };

    // ─── Cancel + back-nav with unsaved-changes guard ────────────────────
    const handleCancel = () => {
        if (hasChanges) {
            confirmDialog({
                message: 'You have unsaved changes. Discard them?',
                header: 'Leave editing?',
                icon: 'pi pi-exclamation-triangle',
                acceptClassName: 'p-button-danger',
                accept: () => navigate('/app/my-agent-profile'),
            });
        } else {
            navigate('/app/my-agent-profile');
        }
    };

    // ─── Photo handlers (preserved from production) ─────────────────────
    const handleProfilePhotoClick = () => profilePhotoInputRef.current?.click();

    const handleProfilePhotoUpload = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        if (!['image/jpeg', 'image/jpg', 'image/png'].includes(file.type)) {
            toast.current?.show({
                severity: 'error',
                summary: 'Invalid File Type',
                detail: 'Please select a JPEG or PNG image',
                life: 3000,
            });
            return;
        }
        if (file.size > 2 * 1024 * 1024) {
            toast.current?.show({
                severity: 'error',
                summary: 'File Too Large',
                detail: 'Image must be less than 2MB',
                life: 3000,
            });
            return;
        }
        try {
            setUploadingPhoto(true);
            const response = await ProfileService.uploadProfilePhoto(file);
            if (response.success) {
                toast.current?.show({
                    severity: 'success',
                    summary: 'Profile photo updated',
                    life: 2500,
                });
                const newPhotoUrl = response.data?.photo_url
                    || response.data?.profile_photo_url_full
                    || response.data?.url;
                setProfilePhotoUrl(newPhotoUrl);
                setPhotoTimestamp(Date.now());
            } else {
                throw new Error(response.message || 'Upload failed');
            }
        } catch (error) {
            const msg = error.response?.data?.message || error.message || 'Failed to upload photo';
            toast.current?.show({ severity: 'error', summary: 'Upload Failed', detail: msg, life: 5000 });
        } finally {
            setUploadingPhoto(false);
            if (profilePhotoInputRef.current) profilePhotoInputRef.current.value = '';
        }
    };

    const handleDeletePhoto = () => {
        confirmDialog({
            message: 'Are you sure you want to delete your profile photo?',
            header: 'Delete Profile Photo',
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                try {
                    setUploadingPhoto(true);
                    const response = await ProfileService.deleteProfilePhoto();
                    if (response.success) {
                        toast.current?.show({
                            severity: 'success',
                            summary: 'Profile photo removed',
                            life: 2500,
                        });
                        setProfilePhotoUrl(null);
                        setPhotoTimestamp(Date.now());
                    } else {
                        throw new Error(response.message || 'Delete failed');
                    }
                } catch (error) {
                    const msg = error.response?.data?.message || error.message || 'Failed to delete photo';
                    toast.current?.show({ severity: 'error', summary: 'Delete Failed', detail: msg, life: 5000 });
                } finally {
                    setUploadingPhoto(false);
                }
            },
        });
    };

    // ─── Cover photo handlers ───────────────────────────────────────────
    const handleCoverPhotoClick = () => coverPhotoInputRef.current?.click();

    const handleCoverPhotoUpload = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            toast.current?.show({
                severity: 'error',
                summary: 'Invalid File',
                detail: 'Please select an image file',
                life: 3000,
            });
            return;
        }
        if (file.size > 3 * 1024 * 1024) {
            toast.current?.show({
                severity: 'error',
                summary: 'File Too Large',
                detail: 'Cover image must be less than 3MB',
                life: 3000,
            });
            return;
        }
        try {
            setUploadingCover(true);
            const response = await ProfileService.uploadCoverPhoto(file);
            if (response.success) {
                toast.current?.show({
                    severity: 'success',
                    summary: 'Cover photo updated',
                    life: 2500,
                });
                const newCoverUrl = response.data?.cover_photo_url_full
                    || response.data?.photo_url
                    || response.data?.url;
                setCoverPhotoUrl(newCoverUrl);
                setCoverTimestamp(Date.now());
            } else {
                throw new Error(response.message || 'Upload failed');
            }
        } catch (error) {
            const msg = error.response?.data?.message || error.message || 'Failed to upload cover photo';
            toast.current?.show({ severity: 'error', summary: 'Upload Failed', detail: msg, life: 5000 });
        } finally {
            setUploadingCover(false);
            if (coverPhotoInputRef.current) coverPhotoInputRef.current.value = '';
        }
    };

    const handleDeleteCoverPhoto = () => {
        confirmDialog({
            message: 'Are you sure you want to remove your cover photo?',
            header: 'Remove Cover Photo',
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                try {
                    setUploadingCover(true);
                    const response = await ProfileService.deleteCoverPhoto();
                    if (response.success) {
                        toast.current?.show({
                            severity: 'success',
                            summary: 'Cover photo removed',
                            life: 2500,
                        });
                        setCoverPhotoUrl(null);
                        setCoverTimestamp(Date.now());
                    } else {
                        throw new Error(response.message || 'Delete failed');
                    }
                } catch (error) {
                    const msg = error.response?.data?.message || error.message || 'Failed to remove cover photo';
                    toast.current?.show({ severity: 'error', summary: 'Delete Failed', detail: msg, life: 5000 });
                } finally {
                    setUploadingCover(false);
                }
            },
        });
    };

    // ─── Live completion + step model ───────────────────────────────────
    const completion = useMemo(() => calcCompletion(formData), [formData]);
    const stepModel = STEPS.map((s) => ({ label: s.label, icon: `pi ${s.icon}` }));
    const currentStep = STEPS[stepIndex];

    // Step body — production wizard steps expect (data, onUpdate); ReviewStep
    // is read-only and doesn't take onUpdate.
    const renderStepBody = () => {
        if (!formData) return null;
        const StepBody = currentStep.Component;
        if (currentStep.key === 'review') {
            return <StepBody data={formData} />;
        }
        // Basic info carries the public/private account switch — this page is
        // where the lending network profile actually gets built (a blank
        // profile row already exists for every user from signup).
        if (currentStep.key === 'basic') {
            return <StepBody data={formData} onUpdate={handleStepData} showAccountVisibility />;
        }
        return <StepBody data={formData} onUpdate={handleStepData} />;
    };

    // ═════════════════════════════════════════════════════════════════════
    // RENDER — loading
    // ═════════════════════════════════════════════════════════════════════
    if (loading) {
        return (
            <>
                <Toast ref={toast} />
                <div className="grid">
                    <div className="col-12">
                        <Card>
                            <Skeleton width="60%" height="2rem" className="mb-3" />
                            <Skeleton width="40%" height="1rem" className="mb-4" />
                            <Skeleton width="100%" height="4rem" className="mb-3" />
                            <Skeleton width="100%" height="20rem" />
                        </Card>
                    </div>
                </div>
            </>
        );
    }

    // ═════════════════════════════════════════════════════════════════════
    // RENDER — main
    // ═════════════════════════════════════════════════════════════════════
    return (
        <>
            <Toast ref={toast} />
            <ConfirmDialog />

            <input
                ref={profilePhotoInputRef}
                type="file"
                accept="image/jpeg,image/png,image/jpg"
                style={{ display: 'none' }}
                onChange={handleProfilePhotoUpload}
            />
            <input
                ref={coverPhotoInputRef}
                type="file"
                accept="image/jpeg,image/png,image/jpg"
                style={{ display: 'none' }}
                onChange={handleCoverPhotoUpload}
            />

            <div className="grid">
                {/* Page heading */}
                <div className="col-12">
                    <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center gap-2 mb-3">
                        <div>
                            <h1 className="text-xl font-semibold text-900 m-0 mb-1">Edit profile</h1>
                            <p className="text-600 text-sm m-0">
                                Step through the {STEPS.length} sections to keep your profile up to date.
                            </p>
                        </div>
                    </div>

                    {/* Live completion meter */}
                    <Card
                        className="shadow-1 mb-3"
                        pt={{ body: { className: 'p-3' }, content: { className: 'p-0' } }}
                    >
                        <div className="flex flex-column md:flex-row md:align-items-center gap-3">
                            <div className="flex-grow-1">
                                <div className="flex justify-content-between align-items-baseline mb-1">
                                    <span className="text-600 text-xs font-medium">Profile completion</span>
                                    <span className="text-900 text-sm font-bold">{completion}%</span>
                                </div>
                                <ProgressBar
                                    value={completion}
                                    showValue={false}
                                    style={{ height: '6px' }}
                                    aria-label="Profile completion"
                                />
                            </div>
                            <div className="flex align-items-center gap-2 flex-shrink-0">
                                {hasChanges ? (
                                    <Tag
                                        icon="pi pi-pencil"
                                        value="Unsaved changes"
                                        severity="warning"
                                        className="text-xs"
                                        style={{ fontSize: '0.65rem', padding: '0.15rem 0.5rem' }}
                                    />
                                ) : (
                                    <Tag
                                        icon="pi pi-check-circle"
                                        value="All changes saved"
                                        severity="success"
                                        className="text-xs"
                                        style={{ fontSize: '0.65rem', padding: '0.15rem 0.5rem' }}
                                    />
                                )}
                            </div>
                        </div>
                    </Card>

                    {/* Step navigator — clickable numbered circles on small screens,
                        PrimeReact Steps on desktop. Tapable Avatars > the bare Steps
                        component on touch devices. */}
                    <div ref={stepsRef}>
                        <Card
                            className="shadow-1 mb-3 block lg:hidden"
                            pt={{ body: { className: 'p-3' }, content: { className: 'p-0' } }}
                        >
                            <div className="flex align-items-center">
                                {STEPS.map((s, i) => {
                                    const isDone   = i < stepIndex;
                                    const isActive = i === stepIndex;
                                    const isFilled = isDone || isActive;
                                    return (
                                        <React.Fragment key={s.key}>
                                            <Avatar
                                                label={String(i + 1)}
                                                shape="circle"
                                                onClick={() => goTo(i)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                        e.preventDefault();
                                                        goTo(i);
                                                    }
                                                }}
                                                role="button"
                                                tabIndex={0}
                                                aria-label={`Go to step ${i + 1}: ${s.label}`}
                                                aria-current={isActive ? 'step' : undefined}
                                                style={{
                                                    width: '2.25rem',
                                                    height: '2.25rem',
                                                    minWidth: '2.25rem',
                                                    fontSize: '0.85rem',
                                                    fontWeight: 700,
                                                    backgroundColor: isFilled ? 'var(--primary-color)' : 'var(--surface-0)',
                                                    color: isFilled ? 'var(--primary-color-text)' : 'var(--text-color)',
                                                    border: isFilled ? 'none' : '1.5px solid var(--surface-300)',
                                                    cursor: 'pointer',
                                                    flexShrink: 0,
                                                    boxShadow: isActive ? '0 0 0 3px var(--primary-100)' : undefined,
                                                    transition: 'all 150ms ease',
                                                }}
                                            />
                                            {i < STEPS.length - 1 && (
                                                <div
                                                    className="flex-1 mx-1"
                                                    style={{
                                                        height: '2px',
                                                        backgroundColor: i < stepIndex ? 'var(--primary-color)' : 'var(--surface-200)',
                                                        transition: 'background-color 150ms ease',
                                                    }}
                                                    aria-hidden="true"
                                                />
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </div>
                            <div className="flex align-items-center justify-content-between mt-3 text-xs">
                                <span className="text-500 font-medium">
                                    Step {stepIndex + 1} of {STEPS.length}
                                </span>
                                <span className="text-900 font-semibold">{currentStep.label}</span>
                            </div>
                        </Card>

                        {/* Desktop: PrimeReact Steps */}
                        <Card
                            className="shadow-1 mb-3 hidden lg:block"
                            pt={{ body: { className: 'p-3' }, content: { className: 'p-0' } }}
                        >
                            <Steps
                                model={stepModel}
                                activeIndex={stepIndex}
                                onSelect={(e) => goTo(e.index)}
                                readOnly={false}
                            />
                        </Card>
                    </div>

                    {/* Active step card */}
                    <Card
                        className="shadow-1 mb-3"
                        pt={{ body: { className: 'p-3 md:p-4' }, content: { className: 'p-0' } }}
                    >
                        {/* Step hero — icon + title + per-step subtitle */}
                        <div className="flex align-items-start gap-2 md:gap-3 mb-3">
                            <div
                                className="flex align-items-center justify-content-center border-round flex-shrink-0"
                                style={{ width: '2rem', height: '2rem', backgroundColor: 'var(--primary-100)' }}
                            >
                                <i
                                    className={`pi ${currentStep.icon} text-primary`}
                                    style={{ fontSize: '0.9rem' }}
                                    aria-hidden="true"
                                />
                            </div>
                            <div className="flex-1 min-w-0">
                                <h2 className="text-900 font-semibold text-sm md:text-base m-0">
                                    {currentStep.label}
                                </h2>
                                <p className="text-600 text-xs m-0 mt-1 line-height-3">
                                    {currentStep.key === 'basic'     && 'Tell us who you are — this is the core of your public profile.'}
                                    {currentStep.key === 'work'      && 'Add your roles. Fields shown depend on your employment type from Basic Info.'}
                                    {currentStep.key === 'education' && 'Schools, colleges, and ongoing courses.'}
                                    {currentStep.key === 'certs'     && 'Optional — but builds trust with bank partners.'}
                                    {currentStep.key === 'skills'    && 'Skills and languages help agents find you and route leads.'}
                                    {currentStep.key === 'review'    && 'Last look before we save. Click any step above to jump back.'}
                                </p>
                            </div>
                            {stepIndex === 0 && completion > 60 && (
                                <Button
                                    label="Jump to review"
                                    icon="pi pi-arrow-right"
                                    iconPos="right"
                                    severity="secondary"
                                    outlined
                                    size="small"
                                    onClick={handleSkipToReview}
                                    className="hidden lg:inline-flex"
                                />
                            )}
                        </div>

                        {/* Photo banner — only on the Basic Info step. Now
                            includes editable cover background AND profile
                            avatar, each with their own change / remove
                            controls. */}
                        {currentStep.key === 'basic' && (
                            <div className="mb-4 border-round overflow-hidden surface-border border-1">
                                {/* Cover banner — clickable to upload, hover-only Remove button */}
                                <div
                                    className="relative"
                                    style={{
                                        height: '8rem',
                                        background: coverPhotoUrl
                                            ? `url(${coverPhotoUrl}?t=${coverTimestamp}) center/cover`
                                            : 'linear-gradient(135deg, var(--primary-400), var(--primary-600))',
                                    }}
                                >
                                    {/* Cover loading overlay */}
                                    {uploadingCover && (
                                        <div
                                            className="absolute top-0 left-0 w-full h-full flex align-items-center justify-content-center"
                                            style={{ backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 5 }}
                                        >
                                            <i className="pi pi-spin pi-spinner text-white text-2xl" />
                                        </div>
                                    )}

                                    {/* Cover action buttons — top-right of the banner */}
                                    <div className="absolute flex gap-2" style={{ top: '0.5rem', right: '0.5rem' }}>
                                        <Button
                                            icon="pi pi-camera"
                                            label={coverPhotoUrl ? 'Change cover' : 'Add cover'}
                                            size="small"
                                            severity="secondary"
                                            onClick={handleCoverPhotoClick}
                                            disabled={uploadingCover}
                                            tooltip={coverPhotoUrl ? 'Change cover image' : 'Upload a cover image'}
                                            tooltipOptions={{ position: 'left' }}
                                            pt={{
                                                root: {
                                                    style: {
                                                        backgroundColor: 'rgba(255,255,255,0.95)',
                                                        color: 'var(--text-color)',
                                                        border: 'none',
                                                        backdropFilter: 'blur(4px)',
                                                        fontSize: '0.75rem',
                                                        padding: '0.35rem 0.75rem',
                                                    },
                                                },
                                            }}
                                        />
                                        {coverPhotoUrl && (
                                            <Button
                                                icon="pi pi-trash"
                                                size="small"
                                                severity="danger"
                                                onClick={handleDeleteCoverPhoto}
                                                disabled={uploadingCover}
                                                tooltip="Remove cover image"
                                                tooltipOptions={{ position: 'left' }}
                                                pt={{
                                                    root: {
                                                        style: {
                                                            backgroundColor: 'rgba(220, 38, 38, 0.9)',
                                                            border: 'none',
                                                            width: '2rem',
                                                            height: '2rem',
                                                            padding: 0,
                                                        },
                                                    },
                                                }}
                                            />
                                        )}
                                    </div>
                                </div>

                                {/* Profile photo + caption + actions (overlaps cover) */}
                                <div
                                    className="p-3 flex flex-column md:flex-row md:align-items-center gap-3"
                                    style={{ marginTop: '-2rem' }}
                                >
                                    <div className="relative flex-shrink-0">
                                        {profilePhotoUrl ? (
                                            <div
                                                style={{
                                                    width: '4.5rem',
                                                    height: '4.5rem',
                                                    borderRadius: '50%',
                                                    border: '3px solid var(--surface-0)',
                                                    overflow: 'hidden',
                                                    backgroundColor: 'var(--surface-0)',
                                                }}
                                            >
                                                <img
                                                    key={`photo-${photoTimestamp}`}
                                                    src={`${profilePhotoUrl}?t=${photoTimestamp}`}
                                                    alt="Profile"
                                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                    onError={() => setProfilePhotoUrl(null)}
                                                />
                                            </div>
                                        ) : (
                                            <Avatar
                                                icon="pi pi-user"
                                                shape="circle"
                                                style={{
                                                    width: '4.5rem',
                                                    height: '4.5rem',
                                                    backgroundColor: 'var(--surface-100)',
                                                    color: 'var(--primary-700)',
                                                    fontSize: '1.5rem',
                                                    border: '3px solid var(--surface-0)',
                                                }}
                                            />
                                        )}
                                        {uploadingPhoto && (
                                            <div
                                                className="absolute top-0 left-0 w-full h-full flex align-items-center justify-content-center"
                                                style={{
                                                    backgroundColor: 'rgba(0,0,0,0.5)',
                                                    borderRadius: '50%',
                                                    zIndex: 10,
                                                }}
                                            >
                                                <i className="pi pi-spin pi-spinner text-white text-xl" />
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-900 font-semibold text-sm">Profile photo</div>
                                        <div className="text-500 text-xs mt-1 line-height-3">
                                            JPEG / PNG up to 2 MB. Use a clear face shot for best results.
                                        </div>
                                    </div>
                                    <div className="flex gap-2 flex-shrink-0">
                                        <Button
                                            label={profilePhotoUrl ? 'Change photo' : 'Upload photo'}
                                            icon="pi pi-camera"
                                            severity="secondary"
                                            outlined
                                            size="small"
                                            onClick={handleProfilePhotoClick}
                                            disabled={uploadingPhoto}
                                        />
                                        {profilePhotoUrl && (
                                            <Button
                                                label="Remove"
                                                severity="secondary"
                                                text
                                                size="small"
                                                onClick={handleDeletePhoto}
                                                disabled={uploadingPhoto}
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        <Divider className="mb-3" />

                        {renderStepBody()}
                    </Card>

                    {/* Sticky bottom action bar */}
                    <div className="sticky bottom-0 pb-2 pt-1" style={{ background: 'var(--surface-ground)' }}>
                        <Card
                            className="shadow-3"
                            pt={{ body: { className: 'p-2 md:p-3' }, content: { className: 'p-0' } }}
                        >
                            <div className="flex align-items-center justify-content-between gap-2 flex-wrap">
                                <Button
                                    label={stepIndex === 0 ? 'Cancel' : 'Back'}
                                    icon={stepIndex === 0 ? 'pi pi-times' : 'pi pi-arrow-left'}
                                    severity="secondary"
                                    outlined
                                    size="small"
                                    onClick={() => (stepIndex === 0 ? handleCancel() : handlePrevious())}
                                    disabled={saving}
                                />
                                <div className="flex align-items-center gap-2 flex-wrap">
                                    {stepIndex < STEPS.length - 1 ? (
                                        <Button
                                            label="Next"
                                            icon="pi pi-arrow-right"
                                            iconPos="right"
                                            size="small"
                                            onClick={handleNext}
                                            disabled={saving}
                                        />
                                    ) : (
                                        <Button
                                            label="Save changes"
                                            icon="pi pi-check"
                                            size="small"
                                            loading={saving}
                                            onClick={handleSubmit}
                                        />
                                    )}
                                </div>
                            </div>
                        </Card>
                    </div>
                </div>
            </div>
        </>
    );
};

export default EditProfile;
